import { useEffect, useState } from "react";
import useSWR, { preload } from "swr";

import SWR_KEYS from "@/constants/swr-keys";
import { base64ToBlobV2 } from "@/helpers/file-helpers";
import { IOrderPdf } from "@/interfaces/order.interfaces";
import orderService from "@/services/order.service";

type UseSwrGetOrderPdfParams = [string, number];

const fetchOrderPdf = ([, id]: UseSwrGetOrderPdfParams) => orderService.getPdf(id.toString());

export const preloadOrderPdf = (orderId: number | string) => {
  const id = Number(orderId);
  if (!id) return;

  Promise.resolve(preload<IOrderPdf>([SWR_KEYS.getOrderPdf, id], fetchOrderPdf)).catch(() => undefined);
};

const useGetOrderPdf = (orderId: number) => {
  const {
    data,
    isValidating,
    error,
  } = useSWR<IOrderPdf, Error, UseSwrGetOrderPdfParams | null>(
    orderId ? [SWR_KEYS.getOrderPdf, orderId] : null,
    fetchOrderPdf,
    { revalidateOnFocus: false }
  );

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!data?.base64) {
      setPdfUrl(null);
      return;
    }

    const blob = base64ToBlobV2(data.base64, "application/pdf");
    const url = URL.createObjectURL(blob);
    setPdfUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [data?.base64]);

  return {
    pdfUrl,
    isLoading: Boolean(orderId) && (isValidating || Boolean(data?.base64 && !pdfUrl)),
    error,
  };
};

export default useGetOrderPdf;
